/**
 * 画面全体のエラーの受け皿(2026-10-05 全端末対応)。
 * 描画中にエラーが起きると React は画面ごと消して真っ白になるため、ここで受け止めて
 * 日本語の案内と「再読み込み」を出す。通知(トースト)などの部品は使わず、ログイン画面と同じ
 * カード(.login-screen / .login-card)だけで表示する(古い端末でも必ず見える)。
 *
 * - AppErrorBoundary: アプリ全体(main.tsx)を包む。ログイン状態の判定やテーマの読み込みで起きたエラーも受ける
 * - RouteErrorScreen: 各画面のエラー(React Router の errorElement)。メニューに戻れるようにする
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

function ErrorCard({ error, onBack }: { error: unknown; onBack?: () => void }) {
  const detail = messageOf(error);
  return (
    <div className="login-screen">
      <div className="login-card" role="alert">
        <div className="brand">CC DIVISION</div>
        <h1>エラーが起きました</h1>
        <p className="hint">画面を表示できませんでした。再読み込みしても直らない場合は、表示されている内容を管理者に伝えてください。</p>
        {detail && <p className="login-error">{detail}</p>}
        <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
          再読み込み
        </button>
        {onBack && (
          <button type="button" className="login-link" onClick={onBack}>
            最初の画面に戻る
          </button>
        )}
      </div>
    </div>
  );
}

type Props = { children: ReactNode };
type State = { error: unknown };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error ?? new Error('不明なエラー') };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[cc-system] 画面のエラー', error, info.componentStack);
  }

  render() {
    if (this.state.error) return <ErrorCard error={this.state.error} />;
    return this.props.children;
  }
}

/** 各画面のエラー(React Router の errorElement)。最初の画面へ戻れる */
export function RouteErrorScreen() {
  const error = useRouteError();
  const navigate = useNavigate();
  console.error('[cc-system] 画面のエラー', error);
  return <ErrorCard error={error} onBack={() => navigate('/', { replace: true })} />;
}
